import {ScrollStrategy, ScrollStrategyOptions} from '@angular/cdk/overlay';
import {DatePipe, NgClass, NgFor, NgIf} from '@angular/common';
import {
    Component,
    ElementRef,
    HostBinding,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewEncapsulation
} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Router} from '@angular/router';
import {environment} from '@env/environment';
import {FuseScrollbarDirective} from '@fuse/directives/scrollbar';
import {AuthService} from '@shared/services/auth.service';
import {ChatPresenceService} from 'app/modules/admin/chat/chat-presence.service';
import {CHAT_PRESENCE_DOT_CLASSES, Chat, Contact} from 'app/modules/admin/chat/chat.types';
import {canAccessTeamChat} from 'app/navigation/nahaus.navigation';
import {combineLatest, of, Subject, switchMap, takeUntil} from 'rxjs';

/**
 * Docked team chat panel on the right edge (WhatsApp-Web style sidebar):
 * live chats with presence dots and unread badges — one click away from
 * anywhere in the app. ENTERPRISE only, like the chat module itself.
 */
@Component({
    selector: 'quick-chat',
    templateUrl: './quick-chat.component.html',
    styleUrls: ['./quick-chat.component.scss'],
    encapsulation: ViewEncapsulation.None,
    exportAs: 'quickChat',
    standalone: true,
    imports: [NgClass, NgIf, NgFor, MatIconModule, MatButtonModule, MatTooltipModule, FuseScrollbarDirective, DatePipe]
})
export class QuickChatComponent implements OnInit, OnDestroy {
    chats: Chat[] = [];
    opened: boolean = false;
    private _scrollStrategy: ScrollStrategy = this._scrollStrategyOptions.block();
    private _overlay: HTMLElement;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _authService: AuthService,
        private _router: Router,
        public presenceService: ChatPresenceService,
        private _scrollStrategyOptions: ScrollStrategyOptions
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        return {
            'quick-chat-opened': this.opened
        };
    }

    /** The team chat is an ENTERPRISE feature — hide the docked bar otherwise */
    get visible(): boolean {
        return canAccessTeamChat(this._authService.tier);
    }

    get currentUid(): string | null {
        return this._authService.authUser?.uid || null;
    }

    get totalUnread(): number {
        const uid = this.currentUid;
        return (this.chats || []).reduce((sum, chat) => sum + (chat.unreadCountBy?.[uid] || 0), 0);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        // Live team chats — follows login/logout, independent of the chat module
        combineLatest([this._authService.authUser$, this._authService.customerID$, this._authService.tier$]).pipe(
            switchMap(([user, customerID, tier]) => {
                if (!user?.uid || (environment.pool && !customerID) || !canAccessTeamChat(tier)) {
                    return of([] as Chat[]);
                }
                const basePath = customerID ? `customers/${customerID}/teamChats` : 'teamChats';
                return this._authService.afs
                    .collection<Chat>(basePath, ref => ref
                        .where('participantIds', 'array-contains', user.uid)
                        .orderBy('updatedAt', 'desc')
                        .limit(30))
                    .valueChanges({idField: 'id'});
            }),
            takeUntil(this._unsubscribeAll),
        ).subscribe({
            next: (chats) => {
                const uid = this.currentUid;
                this.chats = (chats || []).filter((chat) => !(chat.archivedBy || []).includes(uid));
            },
            error: (error) => console.warn('quickChat: chats listener failed', error),
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    open(): void {
        if (this.opened) {
            return;
        }
        this._toggleOpened(true);
    }

    close(): void {
        if (!this.opened) {
            return;
        }
        this._toggleOpened(false);
    }

    toggle(): void {
        if (this.opened) {
            this.close();
        } else {
            this.open();
        }
    }

    /** Collapsed: first click opens the panel — opened: jump into the conversation */
    openChat(chat: Chat): void {
        if (!this.opened) {
            this.open();
            return;
        }
        this.close();
        this._router.navigate(['/chat_intern', chat.id]);
    }

    openFullChat(): void {
        this.close();
        this._router.navigate(['/chat_intern']);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Display helpers
    // -----------------------------------------------------------------------------------------------------

    displayName(chat: Chat): string {
        if (chat.type === 'group' || chat.type === 'ticket') {
            return chat.title || (chat.type === 'ticket' ? 'Ticket-Chat' : 'Teamgruppe');
        }
        return this._otherParticipant(chat)?.name || 'Teammitglied';
    }

    displayAvatar(chat: Chat): string | null {
        if (chat.type === 'group' || chat.type === 'ticket') {
            return null;
        }
        return this._otherParticipant(chat)?.avatar || null;
    }

    unreadOf(chat: Chat): number {
        return chat.unreadCountBy?.[this.currentUid || ''] || 0;
    }

    presenceDotClass(chat: Chat): string {
        return CHAT_PRESENCE_DOT_CLASSES[this.presenceService.statusOf(this.peerUid(chat))];
    }

    peerUid(chat: Chat): string | null {
        if (chat?.type !== 'direct') {
            return null;
        }
        return (chat.participantIds || []).find((id) => id !== this.currentUid) || null;
    }

    lastMessageDate(chat: Chat): Date | null {
        const value = chat.lastMessageAt;
        if (value instanceof Date) {
            return value;
        }
        if (typeof value?.toDate === 'function') {
            return value.toDate();
        }
        return null;
    }

    trackByFn(index: number, item: any): any {
        return item?.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _otherParticipant(chat: Chat): Contact | null {
        return (chat.participants || [])
            .find((participant) => (participant.uid || participant.id) !== this.currentUid) || null;
    }

    private _showOverlay(): void {
        this._hideOverlay();
        this._overlay = this._renderer2.createElement('div');
        if (!this._overlay) {
            return;
        }
        this._overlay.classList.add('quick-chat-overlay');
        this._renderer2.appendChild(this._elementRef.nativeElement.parentElement, this._overlay);
        this._scrollStrategy.enable();
        this._overlay.addEventListener('click', () => {
            this.close();
        });
    }

    private _hideOverlay(): void {
        if (!this._overlay) {
            return;
        }
        this._overlay.parentNode.removeChild(this._overlay);
        this._overlay = null;
        this._scrollStrategy.disable();
    }

    private _toggleOpened(open: boolean): void {
        this.opened = open;
        if (open) {
            this._showOverlay();
        } else {
            this._hideOverlay();
        }
    }
}
